import dayjs from 'dayjs';
import { StaffMembershipEntitlement } from './types';

export type ScheduleMembershipLike = {
    id?: string;
    _id?: string;
    membershipId?: string;
    name?: string;
    period?: number;
    totalSession?: number;
    status?: string;
    BGColor?: string[];
    canMakeScheduleSession?: number;
    remainSession?: number;
    remainPeriod?: number;
    activationDate?: string | null;
    expirationDate?: string | null;
    membershipSharePoolId?: string;
    isSharedMembership?: boolean;
    sharedMembershipRole?: 'OWNER' | 'PARTICIPANT';
    sharedOwner?: StaffMembershipEntitlement['sharedOwner'];
    [key: string]: unknown;
};

export type SharedScheduleWriteContext = {
    membershipSharePoolId: string;
    operationId: string;
};

export type StableSharedScheduleOperation = {
    intentKey: string;
    writeContext: SharedScheduleWriteContext;
};

const SHARED_MEMBERSHIP_FALLBACK_COLORS = ['#648FE4', '#315FB9'];

const getMembershipId = (membership: ScheduleMembershipLike) =>
    String(membership?.id || membership?._id || membership?.membershipId || '').trim();

const getSharedMembershipLabel = (entitlement: StaffMembershipEntitlement) => {
    const membershipName = entitlement.membership.name.trim() || '이름 없는 수강권';
    const ownerName = entitlement.sharedOwner?.name?.trim();
    return ownerName
        ? `${membershipName} · 공유(소유자 ${ownerName})`
        : `${membershipName} · 공유(소유자 확인 필요)`;
};

const getRemainPeriod = (expirationDate: string | null) => {
    if (!expirationDate || !dayjs(expirationDate).isValid()) return 0;
    return Math.max(0, dayjs(expirationDate).diff(dayjs(), 'day') + 1);
};

export const toSharedScheduleMembership = (
    entitlement: StaffMembershipEntitlement,
    existing?: ScheduleMembershipLike,
): ScheduleMembershipLike | null => {
    if (entitlement.access.type !== 'SHARED') return null;

    return {
        ...(existing || {}),
        id: entitlement.membership.id,
        name: getSharedMembershipLabel(entitlement),
        period: entitlement.membership.period,
        totalSession: entitlement.membership.totalSession,
        status: 'active',
        BGColor:
            Array.isArray(existing?.BGColor) && (existing?.BGColor?.length || 0) >= 2
                ? existing?.BGColor
                : SHARED_MEMBERSHIP_FALLBACK_COLORS,
        canMakeScheduleSession: entitlement.usage.bookableRemaining,
        remainSession: entitlement.usage.usageRemaining,
        remainPeriod: getRemainPeriod(entitlement.membership.expirationDate),
        /**
         * 제출 직전 날짜 경고(scheduleDateWarning)가 읽는다. 원본 없이 권리 응답만으로 만든
         * 합성 카드는 이 두 값이 없어 경고가 통째로 빠졌다. 권리 응답 값을 우선하고, 없으면
         * 원본 카드 값을 둔다. `expiryPolicy` 는 권리 응답에 없어 원본이 없으면 비어 있고,
         * 그때 만료 경고는 생략된다(서버가 409 로 거절한다).
         */
        activationDate:
            entitlement.membership.activationDate ??
            (existing?.activationDate as string | null | undefined) ??
            null,
        expirationDate:
            entitlement.membership.expirationDate ??
            (existing?.expirationDate as string | null | undefined) ??
            null,
        membershipSharePoolId: entitlement.access.sharePoolId,
        isSharedMembership: true,
        sharedMembershipRole: entitlement.access.role,
        sharedOwner: entitlement.sharedOwner,
        // 정지 정보: 이용권 요약이 있으면 그것을, 없으면(정지 기능 없는 서버 등) 개인 목록 행의 값을 그대로 둔다.
        // 참여자 행은 개인 목록에 없어서 이 줄이 없으면 정지가 빠진다(생성 확인 창이 안 뜸).
        pause: entitlement.membership.pause ?? existing?.pause,
    };
};

/**
 * 기존 개인 수강권 목록은 그대로 유지하고 공유 권한만 additive 하게 합친다.
 *
 * 예전에는 "이 화면은 공유를 못 쓴다"는 뜻의 includeShared=false 로 공유 원본까지 걸러냈다.
 * 지금은 서버가 1:1·1:2·반복·추가 일정 전부에서 공유 회차 쓰기를 받으므로 제외할 화면이
 * 하나도 없다. 다시 제외해야 하는 화면이 생기면 그때 **왜 못 쓰는지까지** 화면에 알릴 수
 * 있는 형태로 되살린다 — 그냥 목록에서 빼면 직원이 수강권을 중복 발급한다.
 */
export const mergeScheduleMembershipEntitlements = (
    legacyMemberships: ScheduleMembershipLike[] = [],
    entitlements: StaffMembershipEntitlement[] = [],
): ScheduleMembershipLike[] => {
    const sharedEntitlements = entitlements.filter(
        (entitlement) => entitlement.access.type === 'SHARED',
    );

    const entitlementByMembershipId = new Map(
        sharedEntitlements.map((entitlement) => [entitlement.membership.id, entitlement]),
    );
    const merged = legacyMemberships.flatMap((membership) => {
        const entitlement = entitlementByMembershipId.get(getMembershipId(membership));
        if (!entitlement) return [membership];
        entitlementByMembershipId.delete(entitlement.membership.id);
        const sharedMembership = toSharedScheduleMembership(entitlement, membership);
        return sharedMembership ? [sharedMembership] : [];
    });

    entitlementByMembershipId.forEach((entitlement) => {
        const sharedMembership = toSharedScheduleMembership(entitlement);
        if (sharedMembership) merged.push(sharedMembership);
    });
    return merged;
};

const makeOperationId = () => {
    const randomUUID = globalThis.crypto?.randomUUID?.bind(globalThis.crypto);
    if (randomUUID) return randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const getSharedScheduleWriteContext = (
    membership: ScheduleMembershipLike | null | undefined,
): SharedScheduleWriteContext | undefined => {
    const membershipSharePoolId = String(membership?.membershipSharePoolId || '').trim();
    if (!membershipSharePoolId) return undefined;
    return {
        membershipSharePoolId,
        operationId: `admin-web:${makeOperationId()}`,
    };
};

/**
 * 동일한 예약 의도를 재시도할 때는 같은 operationId를 재사용한다.
 * 의도나 공유 풀이 바뀐 경우에만 새 operationId를 발급한다.
 */
export const getStableSharedScheduleOperation = (
    current: StableSharedScheduleOperation | null | undefined,
    intentKey: string,
    membership: ScheduleMembershipLike | null | undefined,
): StableSharedScheduleOperation | undefined => {
    const membershipSharePoolId = String(membership?.membershipSharePoolId || '').trim();
    if (!membershipSharePoolId) return undefined;

    if (
        current?.intentKey === intentKey &&
        current.writeContext.membershipSharePoolId === membershipSharePoolId
    ) {
        return current;
    }

    const writeContext = getSharedScheduleWriteContext(membership);
    return writeContext ? { intentKey, writeContext } : undefined;
};

/**
 * "회원 추가하기"(POST /one-more-schedule) 한 건의 등록 의도를 문자열 하나로 굳힌다.
 *
 * getStableSharedScheduleOperation 에 넘겨 **같은 의도의 재시도가 같은 operationId 를
 * 재사용**하게 하는 것이 유일한 목적이다. 서버는 이 키로 이미 만든 일정을 되찾아 주므로,
 * 푸시·챗봇 실패로 HTTP 만 실패한 뒤 다시 눌러도 회차가 두 번 빠지지 않는다.
 *
 * 담을 것은 **서버가 만들 일정을 결정하는 값 전부**다. 하나라도 빠뜨리면 다른 일정을
 * 만들려는 클릭이 같은 키를 물려받아, 서버가 앞 요청의 결과를 그대로 돌려준다.
 * 반대로 화면 상태(선택 순서·인덱스 등)를 넣으면 같은 일정을 다시 시도할 때 키가 갈려
 * 중복 차감 방지가 풀린다.
 *
 * 엔드포인트가 대상 일정 ID 를 받지 않고 강사·시각으로만 붙이므로 여기에도 넣지 않는다.
 *
 * ⚠️ `membershipId` 는 **요청 body 에 실제로 실리는 값과 같은 규칙으로** 뽑는다(`membership?.id`).
 * 여기서만 `_id`·`membershipId` 까지 받아주는 넓은 정규화를 쓰면, body 는 undefined 로 나가
 * 400 이 되는데 키는 멀쩡히 만들어져 계약이 어긋난다. 키는 "무엇을 보냈는가"를 가리켜야 한다.
 */
export const buildOneMoreScheduleIntentKey = ({
    centerId,
    coachId,
    memberId,
    membership,
    startTime,
    endTime,
}: {
    centerId?: string | null;
    coachId?: string | null;
    memberId?: string | null;
    membership?: ScheduleMembershipLike | null;
    startTime?: string | null;
    endTime?: string | null;
}): string =>
    JSON.stringify({
        kind: 'ONE_MORE_SCHEDULE',
        centerId: String(centerId || ''),
        coachId: String(coachId || ''),
        memberId: String(memberId || ''),
        membershipId: String(membership?.id || ''),
        membershipSharePoolId: String(membership?.membershipSharePoolId || ''),
        startTime: String(startTime || ''),
        endTime: String(endTime || ''),
    });

export type ScheduleMembershipEmptyReason =
    /** 실제로 이 회원에게 등록된 수강권이 없다. */
    | { kind: 'NO_MEMBERSHIP' }
    /** 공유 수강권은 있으나 지금 고른 강사가 그 수강권의 담당 강사가 아니다. */
    | { kind: 'SHARED_NEEDS_OWNER_COACH'; sharedCount: number };

/**
 * "수강권이 하나도 안 뜬다"의 진짜 이유를 가른다.
 *
 * 두 경우를 똑같이 "등록된 수강권이 없는 회원"으로 보여주면 직원은 있는 수강권을
 * 중복 발급하거나 공유 기능이 고장났다고 오인한다. 화면이 이유별로 다른 다음 행동을
 * 제시할 수 있도록 여기서 한 번만 판정한다.
 *
 * 예전에는 '이 수업 방식에서는 공유를 못 쓴다'(SHARED_UNSUPPORTED_MODE)가 세 번째 이유였다.
 * 서버가 모든 등록 경로에서 공유 회차를 받게 되어 그 이유로 빠지는 수강권이 없어졌다.
 * 이제 목록에서 빠지는 공유 수강권은 **담당 강사 불일치 하나뿐**이다.
 */
export const resolveScheduleMembershipEmptyReason = ({
    excludedSharedCount = 0,
}: {
    /** useScheduleMembershipEntitlements가 센 '목록에서 빠진 공유 수강권' 수. */
    excludedSharedCount?: number;
}): ScheduleMembershipEmptyReason =>
    excludedSharedCount > 0
        ? { kind: 'SHARED_NEEDS_OWNER_COACH', sharedCount: excludedSharedCount }
        : { kind: 'NO_MEMBERSHIP' };
